import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faArrowsRotate,
    faFloppyDisk,
} from '@fortawesome/pro-solid-svg-icons';
import {useSnackbar} from 'notistack';
import dayjs, {Dayjs} from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {CalendarEmployeeDto, PlannerViewDto} from 'apiClient/Api';

import {CalendarEventDetail} from './calendarSources';
import {apiErrorMessage} from './errorText';
import EventDetailDialog from './EventDetailDialog';
import PlannerBoard from './PlannerBoard';
import {
    CATEGORY_COLOR,
    DAY_END_HOUR,
    DAY_START_HOUR,
    PlannerCategory,
    PlannerRange,
    plannerDays,
    toPlannerRows,
} from './plannerModel';

/** Schlüssel der gespeicherten Ansicht — muss zu `UserSettingKeys` im Server passen. */
const SETTING_PEOPLE = 'Planner.People';
const SETTING_RANGE = 'Planner.Range';

/** Die Legende — ohne sie sind sechs Farben Rätselraten. */
const LEGEND: {category: PlannerCategory; labelKey: string}[] = [
    {category: 'order', labelKey: 'calendar.sources.order'},
    {category: 'appointment', labelKey: 'calendar.sources.appointment'},
    {category: 'absence', labelKey: 'calendar.sources.absence'},
    {category: 'project', labelKey: 'calendar.sources.project'},
    {category: 'task', labelKey: 'calendar.sources.task'},
    {category: 'outlook', labelKey: 'calendar.sources.o365'},
];

/**
 * Der Planer: eine Planungstafel über mehrere Personen.
 *
 * Eine Zeile je Person, waagerechte Zeitachse. Ganztägiges (Urlaub, Krank) liegt in einem eigenen
 * Band über den Terminen mit Uhrzeit; überlappende Termine stapeln sich, aber nur wenn sie sich
 * tatsächlich überschneiden — eine ruhige Zeile bleibt flach.
 *
 * Die Personenauswahl wird als persönliche Einstellung gespeichert (`Planner.People`) — wer mit
 * denselben Kollegen plant, soll sie nicht jeden Morgen neu zusammenklicken.
 */
export const Planner: React.FC = () => {
    const {t, i18n} = useTranslation();
    useDocumentTitle('planner.title');
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [employees, setEmployees] = useState<CalendarEmployeeDto[]>([]);
    const [people, setPeople] = useState<CalendarEmployeeDto[]>([]);
    const [range, setRange] = useState<PlannerRange>('week');
    const [anchor, setAnchor] = useState<Dayjs>(() => dayjs().startOf('day'));

    const [view, setView] = useState<PlannerViewDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState<CalendarEventDetail | null>(null);

    /*
     * Eigener Uhrzeit-Zustand: der Jetzt-Strahl muss wandern, ohne dass jemand neu lädt — auf einer
     * Planungstafel steht man sonst irgendwann vor einer Linie, die eine halbe Stunde alt ist.
     */
    const [now, setNow] = useState<Dayjs>(() => dayjs());
    useEffect(() => {
        const timer = setInterval(() => setNow(dayjs()), 60_000);
        return () => clearInterval(timer);
    }, []);

    const days = useMemo(() => plannerDays(anchor, range), [anchor, range]);

    /** Zeitraum eines Termins als Text — dieselbe Form wie im Detail-Dialog. */
    const formatPeriod = useCallback(
        (start?: string | null, end?: string | null, allDay?: boolean): string => {
            if (!start) return '—';
            const from = dayjs(start.replace(/(Z|[+-]\d{2}:\d{2})$/, ''));
            const day = from.toDate().toLocaleDateString(i18n.language,
                {weekday: 'short', day: '2-digit', month: '2-digit'});
            if (allDay) return `${day} · ${t('calendar.allDay')}`;
            const to = end ? dayjs(end.replace(/(Z|[+-]\d{2}:\d{2})$/, '')) : null;
            return to ? `${day} ${from.format('HH:mm')}–${to.format('HH:mm')}` : `${day} ${from.format('HH:mm')}`;
        }, [i18n.language, t]);

    const rows = useMemo(
        () => toPlannerRows(view, days, t, formatPeriod),
        [view, days, t, formatPeriod]);

    // Mitarbeiterliste und gespeicherte Ansicht laden.
    useEffect(() => {
        let list: CalendarEmployeeDto[] = [];

        context.api.labelConnect
            .labelConnectGetEmployees()
            .then((response) => {
                list = response.data;
                setEmployees(list);
                return context.api.user.userGetMySetting(SETTING_PEOPLE);
            })
            .then((response) => {
                const stored = parseStoredPeople(response.data.value);
                // Über die Mailadressen zurück auf die Mitarbeiter abbilden: gespeichert werden
                // Adressen, weil Personalnummern zwar stabil, aber für den Server nicht die
                // Adressierung sind.
                setPeople(stored
                    .map((mail) => list.find((e) => matchesMail(e, mail)))
                    .filter((e): e is CalendarEmployeeDto => e !== undefined));
                return context.api.user.userGetMySetting(SETTING_RANGE);
            })
            .then((response) => {
                if (response.data.value === 'day' || response.data.value === 'week') {
                    setRange(response.data.value);
                }
            })
            // Still: ohne gespeicherte Ansicht fängt man mit einer leeren Auswahl an.
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const identifiers = useMemo(
        () => people.map(mailOf).filter((m): m is string => !!m),
        [people]);

    const load = useCallback(() => {
        if (identifiers.length === 0) {
            setView(undefined);
            return;
        }

        setLoading(true);
        setError(undefined);
        context.api.calendar
            .calendarGetPlanner({
                users: identifiers.join(','),
                von: days[0].format('YYYY-MM-DD'),
                bis: days[days.length - 1].format('YYYY-MM-DD'),
            })
            .then((response) => setView(response.data))
            .catch((e: any) => {
                setError(apiErrorMessage(e, t('planner.loadFailed')));
                setView(undefined);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identifiers.join(','), days[0].valueOf(), days[days.length - 1].valueOf()]);

    useEffect(() => {
        load();
    }, [load]);

    const saveView = async (): Promise<void> => {
        setSaving(true);
        try {
            await context.api.user.userSetMySetting(SETTING_PEOPLE, {
                value: JSON.stringify(identifiers),
            });
            await context.api.user.userSetMySetting(SETTING_RANGE, {value: range});
            enqueueSnackbar(t('planner.saved'), {variant: 'success'});
        } catch (e) {
            context.apiError(e);
        } finally {
            setSaving(false);
        }
    };

    const rangeLabel = range === 'day'
        ? days[0].toDate().toLocaleDateString(i18n.language,
            {weekday: 'long', day: '2-digit', month: 'long', year: 'numeric'})
        : `${days[0].format('DD.MM.')} – ${days[6].format('DD.MM.YYYY')}`;


    /**
     * Position der Jetzt-Linie in Prozent, oder `null` wenn der heutige Tag nicht im Blick ist
     * oder die aktuelle Uhrzeit außerhalb des gezeigten Abschnitts liegt. Auf einer Planungstafel
     * ist „wo stehen wir gerade?" die erste Frage.
     */
    const nowPosition = useMemo(() => {
        const dayIndex = days.findIndex((d) => d.isSame(now, 'day'));
        if (dayIndex === -1) return null;

        const minuteOfDay = now.hour() * 60 + now.minute() - DAY_START_HOUR * 60;
        const perDay = (DAY_END_HOUR - DAY_START_HOUR) * 60;
        if (minuteOfDay < 0 || minuteOfDay > perDay) return null;

        return ((dayIndex * perDay + minuteOfDay) / (days.length * perDay)) * 100;
    }, [days, now]);

    return (
        <Grid size={12}>
            <Box sx={{mb: 2.5}}>
                <Typography variant="h5" sx={{fontWeight: 600}}>{t('planner.title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('planner.subtitle')}</Typography>
            </Box>

            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {/*
              * Alles in einer Karte: Bedienung, Tafel und Legende gehören zusammen. Drei gestapelte
              * Kästen lesen sich als drei Dinge.
              */}
            <Paper variant="outlined" sx={{overflow: 'hidden'}}>
                <Stack direction={{xs: 'column', lg: 'row'}} spacing={2}
                       sx={{px: 2, py: 1.75, alignItems: {lg: 'center'}}}>
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={4}
                        sx={{flexGrow: 1, minWidth: 260}}
                        options={employees}
                        value={people}
                        onChange={(_, value) => setPeople(value)}
                        getOptionLabel={(option) => option.name ?? option.labelUser ?? '—'}
                        isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                        renderInput={(params) => (
                            <TextField {...params} label={t('planner.peopleLabel')}
                                       placeholder={people.length === 0 ? t('planner.peoplePlaceholder') : ''}/>
                        )}
                    />

                    <Stack direction="row" spacing={1.5}
                           sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                        <Typography variant="subtitle2" sx={{fontWeight: 600, whiteSpace: 'nowrap'}}>
                            {rangeLabel}
                        </Typography>
                        {loading && <CircularProgress size={16}/>}

                        {/* Blättern als zusammenhängende Gruppe statt dreier loser Knöpfe. */}
                        <ButtonGroup size="small" variant="outlined">
                            <Button onClick={() => setAnchor(anchor.subtract(1, range))}
                                    aria-label={t('calendar.previous')}>
                                <FontAwesomeIcon icon={faAngleLeft}/>
                            </Button>
                            <Button onClick={() => setAnchor(dayjs().startOf('day'))}>
                                {t('calendar.today')}
                            </Button>
                            <Button onClick={() => setAnchor(anchor.add(1, range))}
                                    aria-label={t('calendar.next')}>
                                <FontAwesomeIcon icon={faAngleRight}/>
                            </Button>
                        </ButtonGroup>

                        <ToggleButtonGroup size="small" exclusive value={range}
                                           onChange={(_, value: PlannerRange | null) => value && setRange(value)}>
                            <ToggleButton value="day">{t('planner.rangeDay')}</ToggleButton>
                            <ToggleButton value="week">{t('planner.rangeWeek')}</ToggleButton>
                        </ToggleButtonGroup>

                        <Tooltip title={t('calendar.reload')}>
                            <span>
                                <IconButton size="small" onClick={load} disabled={loading}>
                                    <FontAwesomeIcon icon={faArrowsRotate}/>
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('planner.saveView')}>
                            <span>
                                <IconButton size="small" onClick={saveView}
                                            disabled={saving || identifiers.length === 0}>
                                    <FontAwesomeIcon icon={faFloppyDisk}/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Divider/>

                {identifiers.length === 0 ? (
                    <Box sx={{px: 3, py: 6, textAlign: 'center'}}>
                        <Typography variant="body2" color="text.secondary">{t('planner.empty')}</Typography>
                    </Box>
                ) : rows.length === 0 ? (
                    /*
                      * Personen gewählt, aber keine Zeile: entweder läuft der Abruf noch oder die
                      * Antwort war leer. Beides gehört benannt — eine stumme Fläche sieht wie ein
                      * Fehler aus und sagt nicht, ob man warten soll.
                      */
                    <Box sx={{px: 3, py: 6, textAlign: 'center'}}>
                        <Typography variant="body2" color="text.secondary">
                            {loading ? t('planner.loading') : t('planner.noRows')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{maxHeight: 'calc(100vh - 330px)', overflow: 'auto'}}>
                        <PlannerBoard rows={rows} days={days} range={range}
                                      nowPosition={nowPosition} nowLabel={now.format('HH:mm')}
                                      onSelect={setSelected}/>
                    </Box>
                )}

                <Divider/>

                <Stack direction="row" spacing={2}
                       sx={{px: 2, py: 1.25, flexWrap: 'wrap', rowGap: 1, alignItems: 'center'}}>
                    {LEGEND.map(({category, labelKey}) => (
                        <Stack key={category} direction="row" spacing={0.75} sx={{alignItems: 'center'}}>
                            <Box sx={{
                                width: 10, height: 10, borderRadius: '2px',
                                bgcolor: CATEGORY_COLOR[category],
                            }}/>
                            <Typography variant="caption" color="text.secondary">{t(labelKey)}</Typography>
                        </Stack>
                    ))}
                    <Box sx={{flexGrow: 1}}/>
                    <Typography variant="caption" color="text.secondary">
                        {t('planner.legendFree')} · {t('planner.legendHours', {from: DAY_START_HOUR, to: DAY_END_HOUR})}
                    </Typography>
                </Stack>
            </Paper>

            <EventDetailDialog detail={selected} onClose={() => setSelected(null)}/>
        </Grid>
    );
};

/** Die Adresse, unter der eine Person adressiert wird. */
const mailOf = (employee: CalendarEmployeeDto): string | undefined =>
    employee.mail || employee.mailInternal || employee.labelUser || undefined;

/** Passt die gespeicherte Adresse zu diesem Mitarbeiter? Beide Mail-Domains prüfen. */
const matchesMail = (employee: CalendarEmployeeDto, mail: string): boolean =>
    [employee.mail, employee.mailInternal, employee.labelUser]
        .some((value) => value && value.toLowerCase() === mail.toLowerCase());

/** Liest die gespeicherte Personenliste; unbrauchbarer Inhalt gilt als „nichts gespeichert". */
const parseStoredPeople = (value?: string | null): string[] => {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
    } catch {
        return [];
    }
};

export default Planner;
